import { useToast } from "@chakra-ui/react";
import { ConnectorConfig, ConnectorType } from "@pufflig/prompt-studio-types";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createConnector } from "../../hooks/useCreateConnector";
import { setupSemanticModel } from "../../hooks/useCreateSemanticModel";
import { getFields } from "./ConnectorConfigForms";
import { GenericConfigModal } from "./GenericConfigModal";
import { useAnalytics } from "@/services/posthog/hooks";
import { create_connector_event } from "@/services/posthog/events";

interface ConnectorConfigModalProps {
  selectedConnectorType: ConnectorType | null;
  isOpen: boolean;
  onClose: (connectorId?: string) => void;
  teamId: string | undefined;
}

export const ConnectorConfigModal = ({ selectedConnectorType, isOpen, onClose, teamId }: ConnectorConfigModalProps) => {
  const [formState, setFormState] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const analytics = useAnalytics();

  /* reset form when opened */
  useEffect(() => {
    if (isOpen) {
      setFormState({});
    }
  }, [isOpen]);

  const handleChange = (field: string, value: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const fields = getFields(selectedConnectorType).map((fieldConfig) => ({
    ...fieldConfig,
    value: formState[fieldConfig.id] || "",
    onChange: (value: string) => handleChange(fieldConfig.id, value),
  }));

  const handleSubmit = async () => {
    setIsLoading(true);
    if (selectedConnectorType) {
      try {
        const config = Object.keys(formState).reduce(
          (acc, key) => {
            if (key !== "name" && key !== "description") {
              (acc as any)[key] = formState[key];
            }
            return acc;
          },
          { type: selectedConnectorType } as ConnectorConfig,
        );

        const connectorId = await createConnector(
          {
            name: formState.name,
            description: formState.description,
            config,
          },
          teamId,
        );

        // TODO: test connection

        await setupSemanticModel(connectorId, teamId);
        analytics.capture(create_connector_event, { type: selectedConnectorType });
        onClose(connectorId);
      } catch (error) {
        Sentry.captureException(error);
        toast({
          title: "Error",
          description: "Error creating connector.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey?.[0] === "connectors" });
        setIsLoading(false);
      }
    }
  };

  return (
    <GenericConfigModal
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={() => onClose()}
      onSubmit={handleSubmit}
      connectorType={selectedConnectorType}
      fields={fields}
    />
  );
};
